import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Modal from "./Modal";
import ClosingX from "./ClosingX";
import BButton from "./BButton.mjs";
import ImportTreeView from "./import/ImportTreeView";
import ImportInspector from "./import/ImportInspector";
import ImportFilePreview from "./import/ImportFilePreview.jsx";
import ImportFilterBar from "./import/ImportFilterBar";
import ProgressBar from "./common/ProgressBar.jsx";
import InlineBanner from "./common/InlineBanner.jsx";
import UploadProgressPanel from "./upload/UploadProgressPanel.jsx";
import { useConfirm } from "../context/ConfirmContext";
import { useUploadManager } from "../context/UploadManagerContext";
import { buildImportTree, flattenForImport, countFiles } from "../utils/importTree";
import { filesFromInputFiles, filesFromDataTransfer } from "../utils/folderUpload";
import { expandZipEntries, hasZipEntries, isZipName, ZipImportError, MAX_ARCHIVE_BYTES } from "../utils/zipImport";
import { fmtBytes } from "../utils/format";
import {
    categoryOf,
    categoryCounts,
    applyFiltersToTree,
    flattenVisibleRows,
    setIncludeForSubtree,
    includedBytes,
    tagFoldersByName,
    buildFolderDescriptors,
    applySuggestions,
    acceptAllSuggestions,
    countSuggestions,
    serializeTagging,
    applyTagging,
} from "./import/importHelpers";
import * as FileService from "../services/FileService.mjs";
import * as AIService from "../services/AIService.mjs";
import { getAllAvailableTags } from "../services/TagService.mjs";
import { getWorkspaceHeader } from "../config/workspaceHeader";
import { saveSnapshot, loadSnapshot, clearSnapshot } from "../utils/importPersist";
import { reportError, notify } from "../notifications/errorBus";
import "../css/ImportWizardModal.css";

const DUP_CONCURRENCY = 4;
const HASH_SIZE_CAP = 500 * 1024 * 1024; // skip client hashing above this

const SILENT = { skipGlobalErrorToast: true }; // we show ONE summary, not N toasts

// Wizard steps, in order — drives the header progress indicator.
const STEPS = [
    { key: "select", label: "Select" },
    { key: "organize", label: "Organize" },
    { key: "import", label: "Import" },
];

const CATEGORY_ICON = {
    Audio: "🎵",
    Image: "🖼️",
    "3D Model": "🧊",
    Video: "🎬",
    Document: "📄",
    Archive: "🗜️",
    Other: "📄",
};

/** Compact "Select → Organize → Import" progress indicator in the modal header. */
const StepIndicator = ({ current }) => {
    const idx = STEPS.findIndex((s) => s.key === current);
    return (
        <div className="iw-steps" role="list" aria-label="Import progress">
            {STEPS.map((s, i) => (
                <div
                    key={s.key}
                    role="listitem"
                    className={`iw-step ${i === idx ? "current" : ""} ${i < idx ? "done" : ""}`}
                    aria-current={i === idx ? "step" : undefined}
                >
                    <span className="iw-step-num">{i < idx ? "✓" : i + 1}</span>
                    <span className="iw-step-label">{s.label}</span>
                </div>
            ))}
        </div>
    );
};

/** Run `fn` over `items` with at most `limit` concurrent. */
async function runPool(items, limit, fn) {
    let i = 0;
    const worker = async () => {
        while (i < items.length) {
            const idx = i++;
            await fn(items[idx], idx);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

const ImportWizardModal = ({ onClose, initialEntries }) => {
    const confirm = useConfirm();
    const [step, setStep] = useState("select"); // select | organize | import
    const [isDragOver, setIsDragOver] = useState(false);
    const [extracting, setExtracting] = useState(false); // unpacking a .zip selection
    // Live unpack progress: files done + decompressed bytes vs the declared total.
    const [extractProgress, setExtractProgress] = useState({ files: 0, bytes: 0, totalBytes: 0 });
    // Set when a picked/dropped .zip is too big to unpack in the browser — drives
    // a persistent banner that steers the user to the memory-safe folder path.
    const [largeZipNotice, setLargeZipNotice] = useState(null); // { names: string[], max: number } | null
    const extractShownRef = useRef({ files: 0, bytes: 0 }); // last values pushed to state (throttle)

    // Tree lives in a ref (mutated in place for tag/include edits); a version
    // counter forces re-render so we never deep-clone a large tree per edit.
    const treeRef = useRef(null);
    const [treeVersion, setTreeVersion] = useState(0);
    const bumpTree = useCallback(() => setTreeVersion((v) => v + 1), []);

    const [expanded, setExpanded] = useState(() => new Set());
    const [selectedKey, setSelectedKey] = useState(null);

    const [filters, setFilters] = useState({
        categories: new Set(),
        name: "",
        minMB: "",
        maxMB: "",
        excludeDuplicates: false,
    });
    const [dupChecking, setDupChecking] = useState(false);
    const [dupCount, setDupCount] = useState(null);
    const [aiConfigured, setAiConfigured] = useState(false);
    const [aiRunning, setAiRunning] = useState(false);
    const [hasSavedSession, setHasSavedSession] = useState(false);
    const [snapshotLoaded, setSnapshotLoaded] = useState(false);

    const workspaceId = getWorkspaceHeader();
    const snapshotRef = useRef(null); // tagging snapshot from a previous session
    const saveTimerRef = useRef(null);

    const [idToName, setIdToName] = useState({});
    const [nameToId, setNameToId] = useState({});

    const [createGroup, setCreateGroup] = useState(false);
    const [groupName, setGroupName] = useState("");

    // Import execution lives in the global UploadManager (so it survives this
    // modal closing / the user navigating away). The wizard only kicks it off
    // and then shows the live panel for the returned session.
    const { startImport: enqueueImport } = useUploadManager();
    const [sessionId, setSessionId] = useState(null);

    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const mountedRef = useRef(true);
    const extractAbortRef = useRef(null); // aborts an in-flight zip unpack on close
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(saveTimerRef.current);
            if (extractAbortRef.current) extractAbortRef.current.abort();
        };
    }, []);

    // Load the workspace tag list once for id<->name mapping.
    useEffect(() => {
        (async () => {
            try {
                const tags = await getAllAvailableTags();
                const i2n = {};
                const n2i = {};
                tags.forEach((t) => {
                    i2n[String(t.id)] = t.name;
                    n2i[String(t.name).toLowerCase()] = String(t.id);
                });
                if (mountedRef.current) {
                    setIdToName(i2n);
                    setNameToId(n2i);
                }
            } catch (e) {
                /* tag autocomplete just won't have suggestions */
            }
        })();
    }, []);

    // Is AI auto-tagging available on this server? (drives the ✨ button)
    useEffect(() => {
        AIService.getStatus(SILENT)
            .then((s) => {
                if (mountedRef.current) setAiConfigured(!!s.configured);
            })
            .catch(() => {});
    }, []);

    // Crash-safety: is there saved tagging from a previous (un-imported) session?
    useEffect(() => {
        loadSnapshot(workspaceId)
            .then((snap) => {
                if (!mountedRef.current) return;
                const hasData = snap && (Object.keys(snap.folders || {}).length || Object.keys(snap.files || {}).length);
                if (hasData) {
                    snapshotRef.current = snap;
                    setHasSavedSession(true);
                }
            })
            // Mark "loaded" either way so a folder-drop's initial ingest can run
            // AFTER any saved snapshot is available (avoids the restore race).
            .finally(() => {
                if (mountedRef.current) setSnapshotLoaded(true);
            });
    }, [workspaceId]);

    const discardSavedSession = useCallback(() => {
        snapshotRef.current = null;
        setHasSavedSession(false);
        clearSnapshot(workspaceId);
    }, [workspaceId]);

    // Debounced auto-save of the tagging snapshot (paths only, no file bytes) so
    // a refresh/crash during organize doesn't lose the work.
    useEffect(() => {
        if (step !== "organize" || !treeRef.current) return undefined;
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            // Only persist if the tree still belongs to the active workspace (a
            // workspace switch must not save tree A under workspace B's key).
            if (treeRef.current && treeRef.current._workspaceId === workspaceId) {
                saveSnapshot(workspaceId, serializeTagging(treeRef.current));
            }
        }, 800);
        return () => clearTimeout(saveTimerRef.current);
    }, [treeVersion, step, workspaceId]);

    const filterCriteria = useCallback(
        (f) => ({
            categories: f.categories,
            name: f.name,
            minBytes: f.minMB ? Number(f.minMB) * 1024 * 1024 : 0,
            maxBytes: f.maxMB ? Number(f.maxMB) * 1024 * 1024 : 0,
            excludeDuplicates: f.excludeDuplicates,
        }),
        []
    );

    // ── Step 1: selection ───────────────────────────────────────────────────
    const ingestEntries = useCallback(
        async (entries) => {
            setLargeZipNotice(null); // clear any stale "too large" banner on a fresh pick
            if (!entries || entries.length === 0) {
                notify({ type: "warning", title: "Nothing to import", message: "No files were found in that selection." });
                return;
            }

            // Pre-flight: a .zip is unpacked IN THE BROWSER, so it's hard-capped at
            // MAX_ARCHIVE_BYTES (anything bigger would OOM the tab). Catch oversize
            // archives up front — before flashing the unpack UI — and steer the user
            // to the memory-safe folder path (drag the UNZIPPED folder, no size
            // limit). Drop just the oversize archives; ingest whatever else came in.
            const oversizeZips = entries.filter(
                (e) => e?.file && isZipName(e.relativePath || e.file.name || "") && e.file.size > MAX_ARCHIVE_BYTES
            );
            if (oversizeZips.length > 0) {
                setLargeZipNotice({ names: oversizeZips.map((e) => e.file.name), max: MAX_ARCHIVE_BYTES });
                const oversizeSet = new Set(oversizeZips);
                entries = entries.filter((e) => !oversizeSet.has(e));
                if (entries.length === 0) return; // nothing else to ingest; the banner explains why
            }

            // Expand any picked/dropped .zip archives in place (streaming,
            // size-capped, nested-zip-aware). A selection with no archives passes
            // straight through, so the folder/file path pays nothing.
            let ingestable = entries;
            if (hasZipEntries(entries)) {
                const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
                extractAbortRef.current = controller;
                extractShownRef.current = { files: 0, bytes: 0 };
                setExtractProgress({ files: 0, bytes: 0, totalBytes: 0 });
                setExtracting(true);
                try {
                    ingestable = await expandZipEntries(entries, {
                        signal: controller ? controller.signal : undefined,
                        // Throttle re-renders: advance the bar on the first file,
                        // every 25 files, or whenever the byte fraction moves ≥0.5%
                        // — never once per file in a multi-thousand-file archive.
                        onProgress: (p) => {
                            const last = extractShownRef.current;
                            const byteStep = p.totalBytes > 0 && p.bytes - last.bytes >= p.totalBytes / 200;
                            if (p.files === 1 || p.files - last.files >= 25 || byteStep) {
                                extractShownRef.current = { files: p.files, bytes: p.bytes };
                                if (mountedRef.current) setExtractProgress(p);
                            }
                        },
                    });
                } catch (err) {
                    // Unmounted or user-cancelled mid-unpack → stay silent.
                    if (!mountedRef.current || (err instanceof ZipImportError && err.code === "aborted")) return;
                    if (err instanceof ZipImportError) {
                        notify({ type: "warning", title: "Couldn’t open that archive", message: err.message });
                    } else {
                        reportError(err, { title: "Couldn’t extract the archive" });
                    }
                    return;
                } finally {
                    extractAbortRef.current = null;
                    if (mountedRef.current) setExtracting(false);
                }
                if (!mountedRef.current) return; // modal closed during extraction
                if (!ingestable || ingestable.length === 0) {
                    notify({ type: "warning", title: "Nothing to import", message: "That archive contained no importable files." });
                    return;
                }
            }

            const tree = buildImportTree(ingestable);
            tree._workspaceId = workspaceId; // guard against saving under a switched workspace
            treeRef.current = tree;

            const counts = categoryCounts(tree);
            const initialFilters = {
                categories: new Set(Object.keys(counts)),
                name: "",
                minMB: "",
                maxMB: "",
                excludeDuplicates: false,
            };
            applyFiltersToTree(tree, filterCriteria(initialFilters));
            setFilters(initialFilters);

            // Restore tagging from a previous session, matching by path (applied
            // after filters so per-file deselections are honored).
            if (snapshotRef.current) {
                const restored = applyTagging(tree, snapshotRef.current);
                if (restored > 0) {
                    notify({
                        type: "success",
                        title: "Tagging restored",
                        message: `Re-applied tags to ${restored} item(s) from your previous session.`,
                    });
                }
                snapshotRef.current = null; // consume once — don't re-apply to a different folder
                setHasSavedSession(false);
            }

            setExpanded(new Set([...tree.folders.values()].map((f) => f.path)));
            setSelectedKey(null);
            setDupCount(null);
            setStep("organize");
            bumpTree();
        },
        [bumpTree, filterCriteria, workspaceId]
    );

    // Opened from a whole-browser folder drop → ingest immediately (skip select).
    // Wait for the saved-snapshot load first so a prior session's tagging can be
    // restored (ingestEntries reads snapshotRef synchronously).
    const initialIngestedRef = useRef(false);
    useEffect(() => {
        if (initialIngestedRef.current || !snapshotLoaded) return;
        if (initialEntries && initialEntries.length > 0) {
            initialIngestedRef.current = true;
            ingestEntries(initialEntries);
        }
    }, [initialEntries, ingestEntries, snapshotLoaded]);

    // A cancelled native picker yields an empty FileList — treat as a no-op, not
    // a "nothing to import" warning (which is reserved for a non-empty selection
    // that contained no importable files).
    // Both pickers clear `value` afterwards so re-choosing the SAME folder/files
    // fires change again — without it, backing out and re-picking is a no-op.
    const onFolderPicked = (e) => {
        if (extracting) return;
        const entries = filesFromInputFiles(e.target.files);
        e.target.value = "";
        if (entries.length > 0) ingestEntries(entries);
    };
    const onFilesPicked = (e) => {
        if (extracting) return;
        const entries = filesFromInputFiles(e.target.files);
        e.target.value = "";
        if (entries.length > 0) ingestEntries(entries);
    };
    const onDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (extracting) return;
        const entries = await filesFromDataTransfer(e.dataTransfer);
        ingestEntries(entries);
    };

    // Abort an in-flight zip unpack (the catch in ingestEntries swallows the
    // resulting "aborted" error and the finally clears the extracting flag).
    const cancelExtraction = useCallback(() => {
        if (extractAbortRef.current) extractAbortRef.current.abort();
    }, []);

    // ── Tree interactions ─────────────────────────────────────────────────────
    const counts = useMemo(() => {
        void treeVersion;
        return treeRef.current ? categoryCounts(treeRef.current) : {};
    }, [treeVersion]);
    const rows = useMemo(() => {
        void treeVersion;
        return treeRef.current ? flattenVisibleRows(treeRef.current, expanded) : [];
    }, [treeVersion, expanded]);
    const includedCount = useMemo(() => {
        void treeVersion;
        return treeRef.current ? countFiles(treeRef.current, true) : 0;
    }, [treeVersion]);
    const totalCount = useMemo(() => {
        void treeVersion;
        return treeRef.current ? countFiles(treeRef.current, false) : 0;
    }, [treeVersion]);
    const totalIncludedBytes = useMemo(() => {
        void treeVersion;
        return treeRef.current ? includedBytes(treeRef.current) : 0;
    }, [treeVersion]);

    const toggleExpand = useCallback((path) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const applyInclude = useCallback(
        (row, checked) => {
            if (row.type === "folder") setIncludeForSubtree(row.node, checked);
            else row.node.include = checked;
            bumpTree();
        },
        [bumpTree]
    );

    const onFiltersChange = useCallback(
        (next) => {
            setFilters(next);
            if (treeRef.current) applyFiltersToTree(treeRef.current, filterCriteria(next));
            bumpTree();
        },
        [bumpTree, filterCriteria]
    );

    const autoTagByName = useCallback(() => {
        if (!treeRef.current) return;
        const added = tagFoldersByName(treeRef.current);
        bumpTree();
        notify({
            type: "success",
            title: "Folders tagged",
            message: added > 0 ? `Added ${added} folder tag(s) from folder names.` : "Folders were already tagged.",
        });
    }, [bumpTree]);

    const suggestionCount = useMemo(() => {
        void treeVersion;
        return treeRef.current ? countSuggestions(treeRef.current) : 0;
    }, [treeVersion]);

    const runAiAutoTag = useCallback(async () => {
        if (!treeRef.current) return;
        const descriptors = buildFolderDescriptors(treeRef.current);
        if (descriptors.length === 0) {
            notify({ type: "info", title: "No folders to tag", message: "There are no folders to suggest tags for." });
            return;
        }
        const ok = await confirm({
            title: "Run AI auto-tag?",
            message: `Use AI to suggest tags for ${descriptors.length} folder(s)? This makes about ${descriptors.length} AI request(s) and may incur cost.`,
            confirmText: "Suggest tags",
        });
        if (!ok) return;
        setAiRunning(true);
        let added = 0;
        let failed = false;
        try {
            const AI_CHUNK = 200; // matches backend MAX_FOLDERS_PER_RUN
            for (let i = 0; i < descriptors.length; i += AI_CHUNK) {
                let suggestions;
                try {
                    // eslint-disable-next-line no-await-in-loop -- sequential chunks keep each request within the backend cap
                    suggestions = await AIService.suggestFolderTags(descriptors.slice(i, i + AI_CHUNK), SILENT);
                } catch (err) {
                    // Keep suggestions already applied from earlier chunks (they cost
                    // budget); stop and report rather than discarding everything.
                    failed = true;
                    reportError(err, { title: "AI auto-tag failed for some folders" });
                    break;
                }
                added += applySuggestions(treeRef.current, suggestions);
                if (mountedRef.current) bumpTree();
            }
            notify({
                type: failed ? "warning" : "success",
                title: failed ? "AI auto-tag partially complete" : "AI suggestions ready",
                message:
                    added > 0
                        ? `Suggested ${added} tag(s). Review them per folder, or "Accept all".`
                        : failed
                        ? "No suggestions were applied before the error."
                        : "No new tags were suggested.",
            });
        } finally {
            if (mountedRef.current) setAiRunning(false);
        }
    }, [bumpTree, confirm]);

    const acceptAllAiGlobal = useCallback(() => {
        if (!treeRef.current) return;
        const n = acceptAllSuggestions(treeRef.current);
        bumpTree();
        notify({ type: "success", title: "Suggestions accepted", message: `Accepted ${n} AI tag(s).` });
    }, [bumpTree]);

    // The inspector's selection is DERIVED from the live rows so inherited tags
    // stay correct after an ancestor's tags change (no stale snapshot).
    const inspectorSelected = useMemo(() => {
        void treeVersion;
        const row = selectedKey ? rows.find((r) => r.key === selectedKey) : null;
        if (row) {
            return {
                node: row.node,
                isFolder: row.type === "folder",
                inheritedTags: row.inheritedTags,
                onToggleInclude: (checked) => applyInclude(row, checked),
            };
        }
        // Nothing specific selected → edit the ROOT, so tags/metadata apply to the
        // WHOLE import (the only way to tag a flat, folder-less batch all at once).
        const rootNode = treeRef.current;
        if (!rootNode) return null;
        return {
            node: rootNode,
            isFolder: true,
            isRoot: true,
            inheritedTags: [],
            onToggleInclude: (checked) => applyInclude({ type: "folder", node: rootNode }, checked),
        };
    }, [rows, selectedKey, applyInclude, treeVersion]);

    // A FILE selection (not a folder/root) drives the inline preview panel.
    const previewNode = inspectorSelected && !inspectorSelected.isFolder ? inspectorSelected.node : null;

    // ── Duplicate check (opt-in; hashes only the files that would import) ─────
    const checkDuplicates = useCallback(async () => {
        if (!treeRef.current) return;
        setDupChecking(true);
        const files = [];
        const walk = (node) => {
            // A deselected folder sets include=false on every descendant file
            // (setIncludeForSubtree), so the per-file check below already excludes
            // a deselected folder's whole subtree.
            node.files.forEach((f) => {
                if (f.include !== false) files.push(f);
            });
            node.folders.forEach((c) => walk(c));
        };
        walk(treeRef.current);

        let found = 0;
        let errored = 0;
        const hashOf = async (file) => {
            if (file.size > HASH_SIZE_CAP) return null;
            const buf = await file.arrayBuffer();
            const digest = await crypto.subtle.digest("SHA-256", buf);
            return Array.from(new Uint8Array(digest))
                .map((b) => b.toString(16).padStart(2, "0"))
                .join("");
        };
        try {
            await runPool(files, DUP_CONCURRENCY, async (fileNode) => {
                try {
                    const hash = await hashOf(fileNode.file);
                    const exists = await FileService.checkFileExists(fileNode.name, fileNode.file.size, hash, SILENT);
                    fileNode._duplicate = !!exists;
                    if (exists) found++;
                } catch {
                    fileNode._duplicate = false;
                    errored++;
                }
            });
            if (!mountedRef.current) return;
            setDupCount(found);
            notify({
                type: errored > 0 ? "warning" : "info",
                title: "Duplicate check complete",
                message:
                    `${found} file(s) already exist in this workspace.` +
                    (errored > 0 ? ` ${errored} could not be checked.` : ""),
            });
        } finally {
            if (mountedRef.current) {
                setDupChecking(false);
                bumpTree();
            }
        }
    }, [bumpTree]);

    // ── Import execution (delegated to the global UploadManager) ────────────────
    // The wizard prepares the records and hands them to the manager, which runs
    // the wave upload → chunked create → job polling → failure classification
    // independently of this modal. The live panel (step 3) renders the session.
    const startImport = useCallback(() => {
        // Flush the final tagging now (the debounce may have a pending edit) so a
        // partial-failure resume reflects the latest state. A clean import clears
        // the snapshot inside the manager once the job completes with no failures.
        saveSnapshot(workspaceId, serializeTagging(treeRef.current));
        const flat = flattenForImport(treeRef.current);
        const records = flat.map((r) => ({ ...r, clientKey: crypto.randomUUID() }));
        // Name of the dropped folder / .zip (its root = the top path segment). One
        // top-level folder → its name; several → "N folders"; loose files → none.
        const topLevels = new Set(records.map((r) => (r.folderPath || "").split("/")[0]).filter(Boolean));
        const sourceName =
            topLevels.size === 1 ? [...topLevels][0] : topLevels.size > 1 ? `${topLevels.size} folders` : null;
        const id = enqueueImport({
            records,
            createGroup,
            groupName,
            workspaceId,
            sourceName,
            title: `Importing ${records.length} file${records.length === 1 ? "" : "s"}`,
        });
        setSessionId(id);
        setStep("import");
    }, [enqueueImport, workspaceId, createGroup, groupName]);

    // Closing the wizard no longer stops the upload — it keeps running in the
    // tray, so closing is always safe (just a minimize).
    const requestClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <Modal className="importWizardModal modal--chrome" onClose={requestClose} aria-labelledby="iw-modal-title">
            <div className="modal-header iw-header">
                <h1 id="iw-modal-title">Import files</h1>
                <StepIndicator current={step} />
                <ClosingX onClick={requestClose} />
            </div>

            <div className="modal-body iw-body">
                {step === "select" && (
                    <div
                        className={`iw-dropzone ${isDragOver ? "dragging" : ""}`}
                        onDrop={onDrop}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            setIsDragOver(false);
                        }}
                    >
                        {hasSavedSession && (
                            <InlineBanner
                                variant="info"
                                className="iw-resume-banner"
                                title="Saved tagging available"
                            >
                                Re-select the same folder to restore tags from your previous session.{" "}
                                <button type="button" className="iw-text-btn" onClick={discardSavedSession}>
                                    Discard
                                </button>
                            </InlineBanner>
                        )}
                        {largeZipNotice && (
                            <InlineBanner
                                variant="warning"
                                className="iw-largezip-banner"
                                title="That .zip is too large to unpack here"
                                onDismiss={() => setLargeZipNotice(null)}
                            >
                                {largeZipNotice.names.length === 1 ? (
                                    <>
                                        <strong>{largeZipNotice.names[0]}</strong> is over the {fmtBytes(largeZipNotice.max)} limit for
                                        zips unpacked in the browser.
                                    </>
                                ) : (
                                    <>
                                        {largeZipNotice.names.length} archives are over the {fmtBytes(largeZipNotice.max)} limit for
                                        zips unpacked in the browser.
                                    </>
                                )}{" "}
                                Unzip locally and{" "}
                                <button type="button" className="iw-text-btn" onClick={() => folderInputRef.current?.click()}>
                                    choose the folder
                                </button>{" "}
                                instead — folders upload with no size limit and never load into memory.
                            </InlineBanner>
                        )}
                        {extracting ? (
                            <div className="iw-extracting" role="status" aria-live="polite">
                                <span className="iw-dz-icon">🗜️</span>
                                <p className="iw-extracting-title">Unpacking archive…</p>
                                <ProgressBar
                                    className="iw-extract-bar"
                                    value={extractProgress.totalBytes > 0 ? extractProgress.bytes : undefined}
                                    max={extractProgress.totalBytes || 1}
                                    showPercent
                                />
                                <p className="iw-extracting-sub">
                                    {extractProgress.files.toLocaleString()} file
                                    {extractProgress.files === 1 ? "" : "s"} unpacked
                                    {extractProgress.totalBytes > 0
                                        ? ` · ${fmtBytes(extractProgress.bytes)} of ${fmtBytes(extractProgress.totalBytes)}`
                                        : ""}
                                </p>
                                <BButton className="btn-secondary" onClick={cancelExtraction}>
                                    Cancel
                                </BButton>
                            </div>
                        ) : (
                            <>
                                <span className="iw-dz-icon">📦</span>
                                <p className="iw-dz-title">Drag a folder or .zip of assets here</p>
                                <div className="iw-dz-actions">
                                    <BButton className="btn-primary" onClick={() => folderInputRef.current?.click()}>
                                        Choose folder
                                    </BButton>
                                    <BButton onClick={() => fileInputRef.current?.click()}>Choose files or .zip</BButton>
                                </div>
                                <p className="iw-dz-hint">
                                    Folders keep their structure and upload with no size limit · audio, images, video, 3D &amp; more.
                                    <br />
                                    A .zip is unpacked in your browser, so it's capped at {fmtBytes(MAX_ARCHIVE_BYTES)} — for anything
                                    larger, drag the unzipped folder instead.
                                </p>
                            </>
                        )}
                        <input
                            ref={(el) => {
                                folderInputRef.current = el;
                                if (el) {
                                    el.webkitdirectory = true;
                                    el.directory = true;
                                }
                            }}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={onFolderPicked}
                        />
                        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={onFilesPicked} />
                    </div>
                )}

                {step === "organize" && (
                    <div className="iw-organize">
                        <div className="iw-organize-toolbar">
                            <span className="iw-summary">
                                <strong>{includedCount}</strong> of {totalCount} files · {fmtBytes(totalIncludedBytes)}
                            </span>
                            <div className="iw-toolbar-actions">
                                <BButton onClick={autoTagByName} title="Tag each folder with its name">
                                    Tag folders by name
                                </BButton>
                                {aiConfigured && (
                                    <BButton
                                        onClick={runAiAutoTag}
                                        enabled={!aiRunning && totalCount > 0}
                                        title="Use AI to suggest tags for each folder"
                                    >
                                        {aiRunning ? "Thinking…" : "✨ AI auto-tag"}
                                    </BButton>
                                )}
                                {suggestionCount > 0 && (
                                    <BButton onClick={acceptAllAiGlobal} title="Accept all AI suggestions">
                                        Accept all ✨ ({suggestionCount})
                                    </BButton>
                                )}
                            </div>
                        </div>
                        <ImportFilterBar
                            counts={counts}
                            filters={filters}
                            onChange={onFiltersChange}
                            onCheckDuplicates={checkDuplicates}
                            dupChecking={dupChecking}
                            dupCount={dupCount}
                            disabled={dupChecking}
                        />
                        <div className="iw-organize-main">
                            <ImportTreeView
                                rows={rows}
                                selectedKey={selectedKey}
                                onToggleExpand={toggleExpand}
                                onToggleInclude={applyInclude}
                                onSelect={(row) => setSelectedKey(row.key)}
                            />
                            <div className="iw-side">
                                {previewNode && (
                                    <div className="iw-preview">
                                        <div className="iw-preview-head">
                                            <span className="iw-preview-icon" aria-hidden="true">
                                                {CATEGORY_ICON[categoryOf(previewNode.name)] || "📄"}
                                            </span>
                                            <span className="iw-preview-name" title={previewNode.name}>
                                                {previewNode.name}
                                            </span>
                                        </div>
                                        <div className="iw-preview-stage">
                                            {/* key by selection so the audio player fully tears
                                                down between files instead of reusing its instance */}
                                            <ImportFilePreview key={selectedKey} fileNode={previewNode} />
                                        </div>
                                        <div className="iw-preview-sub">
                                            {categoryOf(previewNode.name)}
                                            {previewNode.file ? ` · ${fmtBytes(previewNode.file.size)}` : ""}
                                        </div>
                                    </div>
                                )}
                                <ImportInspector
                                    selected={inspectorSelected}
                                    idToName={idToName}
                                    nameToId={nameToId}
                                    onChange={bumpTree}
                                    workspaceId={workspaceId}
                                    onSelectRoot={() => setSelectedKey(null)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === "import" && sessionId && (
                    <div className="iw-import">
                        <UploadProgressPanel sessionId={sessionId} onClose={onClose} />
                    </div>
                )}
            </div>

            {step !== "import" && (
                <div className="modal-footer">
                    <div className="modal-actions">
                        {step === "organize" && (
                            <>
                                <label className="iw-group-opt">
                                    <input
                                        type="checkbox"
                                        checked={createGroup}
                                        onChange={(e) => setCreateGroup(e.target.checked)}
                                    />
                                    Add to a new collection
                                </label>
                                {createGroup && (
                                    <input
                                        className="iw-group-name"
                                        placeholder="Collection name"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                    />
                                )}
                                <BButton className="btn-secondary" onClick={() => setStep("select")}>
                                    Back
                                </BButton>
                                <BButton className="btn-primary" onClick={startImport} enabled={includedCount > 0}>
                                    Import {includedCount} file{includedCount === 1 ? "" : "s"}
                                </BButton>
                            </>
                        )}
                        {step === "select" && (
                            <BButton className="btn-secondary" onClick={requestClose}>
                                Cancel
                            </BButton>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ImportWizardModal;

